import { firstValueFrom, Observer } from 'rxjs';
import { ProcessingEngine } from '../../engine.model';
import { OperationDescriptor } from '../../operation.model';
import { ProcessingImage } from '../../processing-image';
import { ProcessService } from '../../../../../services/process.service';
import { MainFacade } from '../../../../main.facade';
import { ProcessType } from '../../../request';
import { Status } from '../../../status';
import { RequestBuildContext } from '../../../jit-tool-registry';
import {
  getAllServerOperations,
  TOOL_BY_OPERATION_ID,
  getServerProcessType,
} from '../../../jit-tool-registry.helpers';

/**
 * Server-side processing engine.
 * Wraps existing JIT backend process endpoints (Crop, Deconvolution, EDoF, etc.).
 *
 * Tool metadata (operations, params, request building) is defined once in
 * jit-tool-registry.ts and derived here via getAllServerOperations().
 */
export class ServerEngine implements ProcessingEngine {
  readonly id = 'server';
  readonly name = 'Server (JIT)';
  readonly operations: OperationDescriptor[] = getAllServerOperations();

  private initialized = false;

  constructor(
    private processService: ProcessService,
    private mainFacade: MainFacade
  ) {}

  isInitialized(): boolean {
    return this.initialized;
  }

  async initialize(): Promise<void> {
    this.initialized = true;
  }

  async execute(
    operation: OperationDescriptor,
    input: ProcessingImage,
    params: Record<string, any>
  ): Promise<ProcessingImage> {
    const tool = TOOL_BY_OPERATION_ID.get(operation.id);
    if (!tool) {
      throw new Error(`No registry entry for operation: ${operation.id}`);
    }

    const processType = getServerProcessType(tool);
    const request = this.buildRequest(operation.id, params);
    const instruction = this.mainFacade.createInstruction(request);
    if (!instruction) {
      throw new Error('No file is currently selected. Server operations require a selected file.');
    }

    const response = await firstValueFrom(this.processService.run(processType, instruction));
    if (!response || !response.response) {
      throw new Error('Server process submission failed — no workflow URL returned.');
    }

    // `run()` answers with an absolute status URL, not a bare id, so take the
    // workflowId out of it and rebuild on this app's API base (jit-ui#124).
    // Passing the URL to createMonitorUrl encoded the whole thing as the
    // workflowId, polling a well-formed but meaningless endpoint.
    const monitorUrl = this.processService.monitorUrlFor(response.response);
    await this.waitForCompletion(monitorUrl);

    return input;
  }

  dispose(): void {
    // Nothing to dispose.
  }

  // --- Private helpers ---

  private buildRequest(operationId: string, params: Record<string, any>): any {
    const tool = TOOL_BY_OPERATION_ID.get(operationId);
    if (!tool || !tool.buildRequestBody) {
      return { ...params };
    }
    const context: RequestBuildContext = {
      getRegions: () => this.getDiagramRegions(),
    };
    const body = tool.buildRequestBody(params, context);
    body.processType = getServerProcessType(tool);
    return body;
  }

  private getDiagramRegions(): any[] {
    try {
      const diagram = this.mainFacade.getDiagram();
      if (diagram && (diagram as any).plotService) {
        return (diagram as any).plotService.getRegionPolygons() || [];
      }
    } catch {
      // No diagram or regions available
    }
    return [];
  }

  private waitForCompletion(monitorUrl: URL): Promise<void> {
    return new Promise((resolve, reject) => {
      const observer: Observer<Status> = {
        next: (_status: Status) => {
          // Still running — do nothing, polling continues
        },
        error: (err) => reject(new Error(`Server process failed: ${err.message || err}`)),
        complete: () => resolve(),
      };
      this.processService.monitor(monitorUrl, observer);
    });
  }
}
